import React, { useContext } from 'react';
import { Badge, IconLink } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetBasketQuantity } from '@/generated/basket';
import { PageType } from '@/generated/data.schemas';
import { useGetLayout } from '@/generated/layout';
import { useGetTranslations } from '@/generated/translations';
import { QuickBasket } from '../QuickBasket';
import styles from './MiniBasket.module.scss';

interface MiniBasketProps {
	disableQuickBasket?: boolean;
}

/**
 * MiniBasket component, used for linking to basket and showing basket count.
 */
export const MiniBasket: React.FunctionComponent<MiniBasketProps> = ({ disableQuickBasket }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: layout } = useGetLayout(segmentationId);
	const staticLinks = layout?.data.staticLinks;
	const { data: translations } = useGetTranslations(segmentationId);
	const basketUrl = staticLinks?.find((link) => link.pageType === PageType.Basket)?.url;
	const { data: quantityResponse } = useGetBasketQuantity(segmentationId);

	const route = {
		area: 'StaticPages',
		pageType: 'Basket',
	};

	const [isOpen, setIsOpen] = React.useState(false);
	const [offset, setOffset] = React.useState<{ x: number; y: number } | null>(null);

	const wrapperRef = React.useRef<HTMLDivElement>(null);
	const timerRef = React.useRef<NodeJS.Timeout>();

	const handleHover = (event: React.MouseEvent): void => {
		if ('ontouchstart' in window) {
			return;
		}

		const { type } = event;

		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		timerRef.current = setTimeout(() => {
			if (!wrapperRef.current) {
				return;
			}

			switch (type) {
				case 'mouseenter': {
					const x = wrapperRef.current.offsetLeft + wrapperRef.current.offsetWidth;
					const y = wrapperRef.current.offsetTop + wrapperRef.current.offsetHeight;

					setOffset({ x, y });
					setIsOpen(true);
					break;
				}
				case 'mouseleave':
					setIsOpen(false);
					break;
				default:
			}
		}, 200);
	};

	const totalQuantity = quantityResponse?.data ?? 0;

	return (
		<div onMouseEnter={handleHover} onMouseLeave={handleHover} ref={wrapperRef}>
			{/* Trigger button */}
			<IconLink
				className={styles.wrapper}
				icon={'basket'}
				label={translations?.data.shared.miniBasketTitle || ''}
				to={basketUrl}
				state={route}
				hideLabelOnMobile
				noRadius
			>
				<Badge className={styles.basketBadge} value={totalQuantity} />
			</IconLink>

			{/* Quick basket */}
			{isOpen && offset && !disableQuickBasket && <QuickBasket offset={offset} />}
		</div>
	);
};
